// src/components/Dashboard.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { DISABLE_AUTH } from '../config/flags';
import { useAuth } from '../hooks/useAuth';
import { useSubscription } from '../hooks/useSubscription';
import { useStripe } from '../hooks/useStripe';
import { getPlanAccess } from '../lib/plans/access';
import {
  Crown,
  MessageCircle,
  TrendingUp,
  CreditCard,
  User,
  Sparkles
} from 'lucide-react';

// ✅ ESM import for browser bundles
import { stripeProducts } from '../stripe-config';

// ——— Local error boundary so the page never white-screens ———
class LocalErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; err?: any }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, err: null };
  }
  static getDerivedStateFromError(error: any) {
    return { hasError: true, err: error };
  }
  componentDidCatch(error: any, info: any) {
    console.error('[Mystara] UI crash (Dashboard):', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 p-6">
          <div className="glass-halo tile-neon p-8 text-center">
            <h2 className="text-xl font-semibold text-primary-ink mb-2">Something went wrong</h2>
            <p className="text-primary-ink/80">We couldn’t load your dashboard. Try refreshing the page.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ——— Main component ———
export function Dashboard() {
  // pull in loading so we can gate when auth is actually enabled
  const { user, profile, loading: isLoadingAuth } = useAuth();
  const { hasActiveSubscription } = useSubscription();

  // Support either API shape from useStripe()
  const stripeHook = useStripe() as any;
  const stripeLoading: boolean = stripeHook.loading;
  const checkoutFn: (args: any) => Promise<any> =
    stripeHook.checkout ?? stripeHook.createCheckoutSession;

  // === BABY-GUARD: only block when auth is ON ===
  if (!DISABLE_AUTH && (isLoadingAuth || !user)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 p-6">
        <div className="glass-halo tile-neon p-8 text-center">
          <h2 className="text-xl font-semibold text-primary-ink mb-2">You’re signed out</h2>
          <p className="text-primary-ink/80 mb-4">Please sign in to view your dashboard.</p>
          <a
            href="/auth"
            className="inline-block rounded-2xl px-5 py-3 glass-cta ring-iris hover-shimmer text-primary-ink"
          >
            Go to Sign In
          </a>
        </div>
      </div>
    );
  }

  // Safe plan access when profile might be null in dev mode
  const planAccess = profile
    ? getPlanAccess(profile)
    : { isTrial: true, isMagic: true, dailyPromptLimit: 9999 };

  const isCosmicUser = hasActiveSubscription();

  const handleStripeCheckout = async () => {
    try {
      const proProduct = Array.isArray(stripeProducts)
        ? stripeProducts.find((p: any) => p?.name === 'Mystara AI PRO')
        : null;
      if (!proProduct || !checkoutFn) return;
      await checkoutFn({
        priceId: proProduct.priceId,
        mode: proProduct.mode,
      });
    } catch (e) {
      console.error('[Mystara] checkout error:', e);
    }
  };

  // Safeguard for stats that reference profile fields that might not exist yet
  const promptCountDaily = (profile as any)?.prompt_count_daily ?? 0;
  const promptsRemaining =
    planAccess.dailyPromptLimit === 9999
      ? '∞'
      : Math.max(0, (planAccess.dailyPromptLimit ?? 0) - (promptCountDaily ?? 0)).toString();

  const stats = [
    {
      title: 'Messages Sent',
      value: isCosmicUser ? '147' : '8',
      icon: MessageCircle
    },
    {
      title: 'Prompts Remaining',
      value: isCosmicUser || planAccess.isTrial ? '∞' : promptsRemaining,
      icon: Sparkles
    },
    {
      title: 'Days Active',
      value: '12',
      icon: TrendingUp
    }
  ];

  return (
    <LocalErrorBoundary>
      <div className="min-h-screen pt-16 bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 p-6 app-auth bg-mystara">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8 glass-halo-strong rounded-2xl p-6"
          >
            <h1 className="heading text-3xl font-bold text-primary-ink text-glow-strong mb-1">
              Dashboard
            </h1>
            <p className="text-primary-ink/80">Manage your manifestation journey</p>
          </motion.div>

          {/* Subscription Status */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="rounded-2xl mb-8 glass-halo-strong p-6"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-full tile-neon">
                  <Crown className="h-7 w-7 text-primary-ink" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-primary-ink">
                    {isCosmicUser ? '🌙 Cosmic Plan' : planAccess.isTrial ? '🌟 Trial Plan' : '✨ Magic Plan'}
                  </h3>
                  <p className="text-primary-ink/80">
                    {isCosmicUser
                      ? 'Unlimited access to all manifestation features'
                      : planAccess.isTrial
                      ? 'Trial access - all features unlocked'
                      : 'Limited access - upgrade to unlock everything'}
                  </p>
                </div>
              </div>

              {!isCosmicUser && !planAccess.isTrial && (
                <button
                  onClick={handleStripeCheckout}
                  disabled={stripeLoading}
                  className={`rounded-2xl px-6 py-3 glass-cta ring-iris hover-shimmer text-primary-ink ${
                    stripeLoading ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                >
                  {stripeLoading ? 'Processing…' : 'Upgrade to Cosmic'}
                </button>
              )}
            </div>
          </motion.div>

          {/* Stats Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.1 + index * 0.06 }}
                className="tile-neon tile-neon-hover p-6 rounded-2xl"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 rounded-full glass-halo">
                    <stat.icon className="h-6 w-6 text-primary-ink" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-primary-ink mb-1">{stat.value}</h3>
                <p className="text-primary-ink/80">{stat.title}</p>
              </motion.div>
            ))}
          </div>

          {/* Account Settings */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Profile Settings */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.25 }}
              className="tile-neon tile-neon-hover p-6 rounded-2xl"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 rounded-full glass-halo">
                  <User className="h-6 w-6 text-primary-ink" />
                </div>
                <h3 className="text-xl font-semibold text-primary-ink">Profile Settings</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-primary-ink mb-2">Email Address</label>
                  <input
                    type="email"
                    value={user?.email || 'dev@mystara.me'}
                    disabled
                    className="w-full px-4 py-3 rounded-xl glass-input text-primary-ink/95"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-ink mb-2">Manifestation Focus</label>
                  <select className="w-full px-4 py-3 rounded-xl glass-input text-primary-ink/95">
                    <option>Career & Success</option>
                    <option>Love & Relationships</option>
                    <option>Health & Wellness</option>
                    <option>Financial Abundance</option>
                    <option>Personal Growth</option>
                  </select>
                </div>

                <button className="w-full rounded-2xl glass-cta ring-iris hover-shimmer text-primary-ink py-3">
                  Update Profile
                </button>
              </div>
            </motion.div>

            {/* Billing Settings */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.3 }}
              className="tile-neon tile-neon-hover p-6 rounded-2xl"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 rounded-full glass-halo">
                  <CreditCard className="h-6 w-6 text-primary-ink" />
                </div>
                <h3 className="text-xl font-semibold text-primary-ink">Billing Settings</h3>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-xl glass-halo">
                  <div>
                    <p className="font-medium text-primary-ink">
                      {isCosmicUser ? '🌙 Cosmic Plan' : planAccess.isTrial ? '🌟 Trial Plan' : '✨ Magic Plan'}
                    </p>
                    <p className="text-sm text-primary-ink/80">
                      {isCosmicUser ? '$9.99/month' : planAccess.isTrial ? 'Trial active' : 'No charges'}
                    </p>
                  </div>
                  <div
                    className={`px-3 py-1 rounded-full text-sm ${
                      isCosmicUser
                        ? 'bg-green-100/70 text-green-700'
                        : planAccess.isTrial
                        ? 'bg-blue-100/70 text-blue-700'
                        : 'bg-gray-100/70 text-gray-700'
                    }`}
                  >
                    {isCosmicUser ? 'Active' : planAccess.isTrial ? 'Trial' : 'Magic'}
                  </div>
                </div>

                {isCosmicUser ? (
                  <div className="space-y-2">
                    <button className="w-full text-left p-3 rounded-lg hover:translate-x-[1px] transition-transform text-primary-ink/90 glass-halo">
                      Update payment method
                    </button>
                    <button className="w-full text-left p-3 rounded-lg hover:translate-x-[1px] transition-transform text-primary-ink/90 glass-halo">
                      Download invoices
                    </button>
                    <button className="w-full text-left p-3 rounded-lg hover:translate-x-[1px] transition-transform text-red-600 glass-halo">
                      Cancel subscription
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleStripeCheckout}
                    disabled={stripeLoading}
                    className={`w-full rounded-2xl glass-cta ring-iris hover-shimmer text-primary-ink py-3 ${
                      stripeLoading ? 'opacity-60 cursor-not-allowed' : ''
                    }`}
                  >
                    {stripeLoading ? 'Processing…' : 'Upgrade to Cosmic - $9.99/month'}
                  </button>
                )}
              </div>
            </motion.div>
          </div>

          {/* Progress Section */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.35 }}
            className="mt-8 tile-neon tile-neon-hover p-6 rounded-2xl"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 rounded-full glass-halo">
                <TrendingUp className="h-6 w-6 text-primary-ink" />
              </div>
              <h3 className="text-xl font-semibold text-primary-ink">Manifestation Progress</h3>
            </div>

            <div className="space-y-6">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-primary-ink/90">Daily Practice Streak</span>
                  <span className="text-primary-ink font-semibold">12 days</span>
                </div>
                <div className="w-full rounded-full h-3 glass-halo">
                  <div
                    className="h-3 rounded-full"
                    style={{
                      width: '80%',
                      background:
                        'linear-gradient(90deg, rgba(255,132,193,0.9), rgba(160,196,255,0.9))'
                    }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-primary-ink/90">Goals Manifested</span>
                  <span className="text-primary-ink font-semibold">3 of 5</span>
                </div>
                <div className="w-full rounded-full h-3 glass-halo">
                  <div
                    className="h-3 rounded-full"
                    style={{
                      width: '60%',
                      background:
                        'linear-gradient(90deg, rgba(255,132,193,0.9), rgba(160,196,255,0.9))'
                    }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-primary-ink/90">Gratitude Entries</span>
                  <span className="text-primary-ink font-semibold">24 this month</span>
                </div>
                <div className="w-full rounded-full h-3 glass-halo">
                  <div
                    className="h-3 rounded-full"
                    style={{
                      width: '90%',
                      background:
                        'linear-gradient(90deg, rgba(255,132,193,0.9), rgba(160,196,255,0.9))'
                    }}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </LocalErrorBoundary>
  );
}

// Optional default export that ensures the boundary is always applied
export default function DashboardPage() {
  return <Dashboard />;
}
